/**
 * Failure of an API call, carrying the HTTP status and the envelope's stable
 * machine-readable code (null when the response had no envelope) so callers
 * can branch on specific failures without parsing human-readable messages.
 *
 * `details` is the envelope's code-specific context (null unless the code
 * documents a shape — 402 carries the entitlement shortfall, 503
 * `serving_unavailable` carries `{ state }`), `retryAfterSeconds` is the
 * response's Retry-After when the server sent one, and `requestId` is the
 * correlation handle support can quote. All are optional: a caller that does
 * not branch on them is unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;
  readonly retryAfterSeconds: number | null;
  readonly requestId: string | null;

  constructor(
    message: string,
    status: number,
    code: string | null,
    details: unknown = null,
    retryAfterSeconds: number | null = null,
    requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfterSeconds = retryAfterSeconds;
    this.requestId = requestId;
  }
}

/** HTTP Retry-After as an unclamped delay; callers decide their own retry budget. */
export function parseApiRetryAfterSeconds(
  headerValue: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (typeof headerValue !== "string" || headerValue.trim() === "") return null;
  const raw = headerValue.trim();
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return Math.max(0, Math.ceil(asNumber));
  const asDate = Date.parse(raw);
  if (Number.isNaN(asDate)) return null;
  return Math.max(0, Math.ceil((asDate - nowMs) / 1000));
}
